/**
 * Colonne de gauche de la page « Gammes » : un champ de recherche, puis la liste compacte des
 * gammes. Chaque gamme tient en quelques lignes (libellé, code et marque, modèles, version en
 * vigueur) pour qu'on en voie beaucoup à la fois ; le détail se lit à droite, sans changer de page.
 *
 * Ce composant ne filtre ni ne charge rien : il présente ce qu'on lui donne.
 */
import type { GammeResume } from '../api/client';
import { dateFr } from '../domaine/gammes';
import { libelleCritere } from '../domaine/gammes-identite';

export function ListeGammes({
  gammes,
  filtrees,
  recherche,
  onRecherche,
  selectionnee,
  onSelection,
  afficherArchivees,
  onArchivees,
  erreur,
  verrouillee = false,
}: {
  /** Toutes les gammes chargées ; `null` tant qu'elles ne le sont pas. */
  gammes: readonly GammeResume[] | null;
  /** Celles qui répondent à la recherche. */
  filtrees: readonly GammeResume[];
  recherche: string;
  onRecherche: (saisie: string) => void;
  selectionnee: string | null;
  onSelection: (id: string) => void;
  afficherArchivees: boolean;
  onArchivees: (afficher: boolean) => void;
  erreur: string | null;
  /**
   * Un brouillon est en cours d'édition : on ne change ni de gamme ni de recherche, sans quoi la
   * colonne de droite — et le travail non enregistré qu'elle porte — disparaîtrait.
   */
  verrouillee?: boolean;
}) {
  const total = gammes?.length ?? 0;

  return (
    <div className="carte gammes-liste">
      <div className="gammes-liste-entete">
        <input
          type="text"
          autoFocus
          aria-label="Rechercher une gamme"
          placeholder="Rechercher : modèle, marque, code, référence…"
          value={recherche}
          disabled={verrouillee}
          onChange={(e) => onRecherche(e.target.value)}
        />
        {verrouillee && (
          <p className="aide">
            Un brouillon est en cours de modification : enregistrez ou fermez l’éditeur pour changer de gamme.
          </p>
        )}
        <div className="gammes-liste-outils">
          <span className="aide">
            {gammes === null
              ? 'Chargement…'
              : filtrees.length === total
                ? `${total} gamme${total > 1 ? 's' : ''}`
                : `${filtrees.length} sur ${total}`}
          </span>
          <label className="case">
            <input
              type="checkbox"
              checked={afficherArchivees}
              disabled={verrouillee}
              onChange={(e) => onArchivees(e.target.checked)}
            />
            Afficher les archivées
          </label>
        </div>
      </div>

      <div className="gammes-liste-corps">
        {erreur && <div className="message erreur">{erreur}</div>}

        {gammes === null ? (
          !erreur && <div className="vide">Chargement…</div>
        ) : total === 0 ? (
          <div className="vide">Aucune gamme d’entretien pour l’instant.</div>
        ) : filtrees.length === 0 ? (
          <div className="vide">Aucune gamme ne correspond à « {recherche.trim()} ».</div>
        ) : (
          <ul className="parc-lignes">
            {filtrees.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  className={`parc-ligne${g.id === selectionnee ? ' actif' : ''}`}
                  aria-pressed={g.id === selectionnee}
                  disabled={verrouillee}
                  onClick={() => onSelection(g.id)}
                >
                  <div className="parc-ligne-titre">
                    <strong>{g.label}</strong>
                    {g.archivedAt && <span className="etat revoque">Archivée</span>}
                  </div>
                  <div className="aide">
                    <code>{g.code}</code> · {g.brandName}
                  </div>
                  <div className="aide">
                    {g.criteres.length === 0 ? 'Aucun modèle' : g.criteres.map(libelleCritere).join(', ')}
                  </div>
                  <div className="gammes-ligne-etat">
                    {g.enVigueur ? (
                      <span className="aide">
                        v{g.enVigueur.versionNumber} depuis le {dateFr(g.enVigueur.validFrom)}
                      </span>
                    ) : (
                      <span className="etat attente">Aucune version publiée</span>
                    )}
                    {g.brouillons > 0 && (
                      <span className="aide">
                        · {g.brouillons} brouillon{g.brouillons > 1 ? 's' : ''}
                      </span>
                    )}
                    <span className="aide">
                      · {g.machinesRattachees} machine{g.machinesRattachees > 1 ? 's' : ''}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
